import { StateCreator, create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { TypeContractWorkInfoForm } from '~/components/Contract/WorkInfoForm';

import type { TypeCenterShiftHour, TypeCodeState, TypeContractUserInfoForm } from '~/types';

const initialState = {
  userInfo: {
    name: '',
    juminNo: '',
    postCode: '',
    address: '',
    detailAddress: '',
    financialInstitution: null,
    accountNo: '',
    isVerified: null,
  },
  workInfo: {
    cluster: null,
    center: null,
    teamName: null,
    centerShiftHourType: null,
    workPart: null,
    masterContractSeq: -1,
    masterContractVersionSeq: -1,
    availableContractStartTime: '',
    availableContractEndTime: '',
  },
  optionList: {
    centerOptions: null,
    workPartOptions: null,
    teamNameOptions: null,
    kurlyWorksWorkShiftTypeOptions: null,
    kurlyWorksWorkShiftSet: null,
    kurlyCenterShiftOptions: null,
    availableContractTimeSet: null,
  },
};

type TypeUserInfoSlice = {
  userInfo: TypeContractUserInfoForm;
  actions: {
    setContractInputUserInfo: (userInfo: TypeContractUserInfoForm) => void;
    resetContractInputUserInfo: () => void;
  };
};

/** @description 계약 체결 정보 (아르바이트) 페이지에서 사용자가 입력한 정보를 가지고 있는 상태 */
const userInfoSlice: StateCreator<TypeUserInfoSlice> = (set) => ({
  userInfo: initialState.userInfo,
  actions: {
    setContractInputUserInfo: (userInfo: TypeContractUserInfoForm) => set(() => ({ userInfo })),
    resetContractInputUserInfo: () => set(() => ({ userInfo: initialState.userInfo })),
  },
});

type TypeWorkInfoSlice = {
  workInfo: TypeContractWorkInfoForm;
  actions: {
    hasContractInputWorkInfo: () => boolean;
    setContractInputWorkInfo: (workInfo: TypeContractWorkInfoForm) => void;
    resetContractInputWorkInfo: () => void;
  };
};

/** @description 계약 체결 정보 (근무정보) 페이지에서 사용자가 입력한 정보를 가지고 있는 상태 */
const workInfoSlice: StateCreator<TypeWorkInfoSlice> = (set, get) => ({
  workInfo: initialState.workInfo,
  actions: {
    hasContractInputWorkInfo: () => {
      const workInfo = get().workInfo;

      return Object.values(workInfo).some((value) => value !== -1 && value !== '' && value !== null);
    },
    setContractInputWorkInfo: (workInfo: TypeContractWorkInfoForm) => set(() => ({ workInfo })),
    resetContractInputWorkInfo: () => set(() => ({ workInfo: initialState.workInfo })),
  },
});

/** @description 계약 체결 정보 중 옵션리스트 정보 */
export type TypeContractOptionList = {
  /** 센터 옵션 리스트 */
  centerOptions: TypeCodeState[] | null;
  /** 근무 파트 옵션 리스트 */
  workPartOptions: TypeCodeState[] | null;
  /** 팀명 옵션 리스트 */
  teamNameOptions: TypeCodeState[] | null;
  /** 컬리웍스의 근무 시간 타입 옵션 리스트 */
  kurlyWorksWorkShiftTypeOptions: TypeCodeState[] | null;
  /** 컬리로 근무 Shift에 따른 컬리웍스 고유 아이디 Set */
  kurlyWorksWorkShiftSet: {
    [key: string]: {
      masterContractSeq: number;
      masterContractVersionSeq: number;
    };
  } | null;
  /** 컬리로 근무 시간 타입 오션 리스트 */
  kurlyCenterShiftOptions: TypeCenterShiftHour[] | null;
  /** 컬리로 근무 Shift에 따른 계약가능 시작시간과 종료시간 Set */
  availableContractTimeSet: {
    [key: string]: {
      availableContractStartTime: string;
      availableContractEndTime: string;
    };
  } | null;
};

type TypeOptionListSlice = {
  optionList: TypeContractOptionList;
  actions: {
    setContractInputOptionList: (optionList: TypeContractOptionList) => void;
    resetContractInputOptionList: () => void;
  };
};

/** @description 계약 프로세스 내 옵션으로 제공하는 정보를 가지고 있는 상태 */
const optionListSlice: StateCreator<TypeOptionListSlice> = (set) => ({
  optionList: initialState.optionList,
  actions: {
    setContractInputOptionList: (optionList: TypeContractOptionList) => set(() => ({ optionList })),
    resetContractInputOptionList: () => set(() => ({ optionList: initialState.optionList })),
  },
});

type TypeContractInputStore = TypeUserInfoSlice &
  TypeWorkInfoSlice &
  TypeOptionListSlice & { actions: { resetContractInputState: () => void } };

/**
 * @description 작업자가 입력한 계약 정보를 가지고 있는 상태
 * @remarks 계약이 완료되거나 로그아웃 시 초기화
 */
export const useContractInputStore = create<TypeContractInputStore>()(
  devtools(
    (...a) => {
      const userInfo = userInfoSlice(...a);
      const workInfo = workInfoSlice(...a);
      const optionList = optionListSlice(...a);

      return {
        ...userInfo,
        ...workInfo,
        ...optionList,
        actions: {
          ...userInfo.actions,
          ...workInfo.actions,
          ...optionList.actions,
          resetContractInputState: () => {
            userInfo.actions.resetContractInputUserInfo();
            workInfo.actions.resetContractInputWorkInfo();
            optionList.actions.resetContractInputOptionList();
          },
        },
      };
    },
    { name: 'contractInputStore' }
  )
);
